// M3 primary workflow: polygon → OLT → Auto-design.
// Shown as the first tab of the left panel so users reach for it before
// learning how to manually place nodes/segments.

import { useState } from 'react';
import type { Plant } from '../types/plant';

export type PlantSpecs = {
  split_ratio: '1x4' | '1x8' | '1x16' | '1x32' | '1x64' | '1x4_1x8';
  vault_spacing_ft: number;          // max spacing between handholes along a feeder
  drop_buffer_ft: number;            // buildings within this distance of distribution get a drop
  max_drop_ft: number;               // max acceptable drop length
  default_placement: 'aerial' | 'buried' | 'conduit' | 'direct_bury' | 'micro_trench';
  strands_per_home: number;
  avoid_railroads: boolean;
  avoid_waterways: boolean;
  corridor_padding_ft: number;       // buffer around polygon to include roads
};

export const DEFAULT_SPECS: PlantSpecs = {
  split_ratio: '1x32',
  vault_spacing_ft: 500,
  drop_buffer_ft: 200,
  max_drop_ft: 300,
  default_placement: 'buried',
  strands_per_home: 1,
  avoid_railroads: true,
  avoid_waterways: true,
  corridor_padding_ft: 300,
};

type Props = {
  plant: Plant | null;
  polygonReady: boolean;
  polygonVertexCount: number;
  polygonActive: boolean;
  oltReady: boolean;
  specs: PlantSpecs;
  onSpecsChange: (s: PlantSpecs) => void;
  onStartPolygon: () => void;
  onFinishPolygon: () => void;
  onCancelPolygon: () => void;
  onPlaceOlt: () => void;
  onRunAutoDesign: () => void;
  onClearDesign: () => void;
  onResetPlant: () => void;
  busy: boolean;
  resetBusy: boolean;
  designStatus: string | null;
};

export default function DesignPanel(p: Props) {
  const [showSpecs, setShowSpecs] = useState(false);

  return (
    <div className="design-panel">
      <div className="design-panel__step">
        <StepNum n={1} done={p.polygonReady} />
        <div style={{ flex: 1 }}>
          <div className="design-panel__step-title">Draw the service area</div>
          <div className="design-panel__step-hint">
            Click 3+ points on the map. When you're done, click <b>Finish polygon</b>.
          </div>
          <div style={{ marginTop: 6, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {p.polygonReady ? (
              <button className="btn" onClick={p.onCancelPolygon}>Redraw polygon</button>
            ) : p.polygonActive ? (
              <>
                <button
                  className="btn btn--primary"
                  onClick={p.onFinishPolygon}
                  disabled={p.polygonVertexCount < 3}
                  style={{ opacity: p.polygonVertexCount < 3 ? 0.5 : 1 }}
                >
                  ✓ Finish polygon ({p.polygonVertexCount} pts)
                </button>
                <button className="btn" onClick={p.onCancelPolygon}>Cancel</button>
              </>
            ) : (
              <button className="btn btn--primary" onClick={p.onStartPolygon}>
                Draw polygon
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="design-panel__step">
        <StepNum n={2} done={p.oltReady} />
        <div style={{ flex: 1 }}>
          <div className="design-panel__step-title">Place the OLT / head-end</div>
          <div className="design-panel__step-hint">
            Pick where the fiber starts — usually the existing head-end or cabinet.
          </div>
          <div style={{ marginTop: 6 }}>
            <button className="btn" onClick={p.onPlaceOlt}>
              {p.oltReady ? 'Replace OLT' : 'Place OLT'}
            </button>
          </div>
        </div>
      </div>

      <div className="design-panel__step">
        <StepNum n={3} done={false} active={p.polygonReady && p.oltReady} />
        <div style={{ flex: 1 }}>
          <div className="design-panel__step-title">Run auto-design</div>
          <div className="design-panel__step-hint">
            Feeder + distribution + splitters + drops to every building — one click.
          </div>
          <div style={{ marginTop: 6, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              className="btn btn--primary"
              disabled={p.busy}
              onClick={p.onRunAutoDesign}
              style={{
                opacity: (p.polygonReady && p.oltReady) ? 1 : 0.6,
                cursor: p.busy ? 'wait' : 'pointer',
              }}
            >
              {p.busy ? 'Designing…' : '✨ Auto-design network'}
            </button>
            <button className="btn" onClick={p.onClearDesign}>Reset pins</button>
            {/* Clear Plant moved to Settings → Optical → Danger Zone (typed confirmation).
                Keeping it out of the default flight path so nobody fat-fingers it. */}
          </div>
          {p.designStatus && (
            <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-muted)' }}>
              {p.designStatus}
            </div>
          )}
        </div>
      </div>

      <hr style={{ border: 0, borderTop: '1px solid var(--border)', margin: '8px 0' }} />

      <div>
        <button
          className="design-panel__specs-toggle"
          onClick={() => setShowSpecs((s) => !s)}
        >
          {showSpecs ? '▾' : '▸'} Plant specs / constraints
        </button>
        {showSpecs && <SpecsForm specs={p.specs} onChange={p.onSpecsChange} />}
      </div>
    </div>
  );
}

function StepNum({ n, done, active = false }: { n: number; done: boolean; active?: boolean }) {
  return (
    <div className={`design-step-num ${done ? 'done' : active ? 'active' : ''}`}>
      {done ? '✓' : n}
    </div>
  );
}

function SpecsForm({ specs, onChange }: { specs: PlantSpecs; onChange: (s: PlantSpecs) => void }) {
  const set = <K extends keyof PlantSpecs>(k: K, v: PlantSpecs[K]) => onChange({ ...specs, [k]: v });

  return (
    <div className="specs-form">
      <Row>
        <Lbl>Splitter architecture</Lbl>
        <select value={specs.split_ratio} onChange={(e) => set('split_ratio', e.target.value as any)}>
          <option value="1x32">1×32 (single stage)</option>
          <option value="1x16">1×16</option>
          <option value="1x64">1×64</option>
          <option value="1x4_1x8">1×4 → 1×8 cascaded</option>
          <option value="1x4">1×4</option>
          <option value="1x8">1×8</option>
        </select>
      </Row>
      <Row>
        <Lbl>Default placement</Lbl>
        <select value={specs.default_placement} onChange={(e) => set('default_placement', e.target.value as any)}>
          <option value="buried">Buried</option>
          <option value="aerial">Aerial</option>
          <option value="conduit">Conduit</option>
          <option value="direct_bury">Direct bury</option>
          <option value="micro_trench">Micro trench</option>
        </select>
      </Row>
      <NumRow lbl="Strands per home"    value={specs.strands_per_home}   onChange={(v) => set('strands_per_home', v)}     min={1}  max={24} />
      <NumRow lbl="Vault spacing (ft)"  value={specs.vault_spacing_ft}   onChange={(v) => set('vault_spacing_ft', v)}     min={200} max={2000} />
      <NumRow lbl="Drop buffer (ft)"    value={specs.drop_buffer_ft}     onChange={(v) => set('drop_buffer_ft', v)}       min={20}  max={1000} />
      <NumRow lbl="Max drop length (ft)" value={specs.max_drop_ft}       onChange={(v) => set('max_drop_ft', v)}          min={20}  max={1500} />
      <NumRow lbl="Corridor padding (ft)" value={specs.corridor_padding_ft} onChange={(v) => set('corridor_padding_ft', v)} min={0} max={2000} />
      <Row>
        <label className="map-check">
          <input type="checkbox" checked={specs.avoid_railroads} onChange={(e) => set('avoid_railroads', e.target.checked)} />
          <span>Penalize railroad crossings</span>
        </label>
      </Row>
      <Row>
        <label className="map-check">
          <input type="checkbox" checked={specs.avoid_waterways} onChange={(e) => set('avoid_waterways', e.target.checked)} />
          <span>Penalize waterway crossings</span>
        </label>
      </Row>
    </div>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return <div className="specs-form__row">{children}</div>;
}
function Lbl({ children }: { children: React.ReactNode }) {
  return <label className="specs-form__lbl">{children}</label>;
}
function NumRow({ lbl, value, onChange, min, max }: { lbl: string; value: number; onChange: (v: number) => void; min: number; max: number }) {
  return (
    <Row>
      <Lbl>{lbl}</Lbl>
      <input
        type="number" min={min} max={max} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </Row>
  );
}
